import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Snackbar } from '@mui/material';
import { userBookmarkService } from '../services/userBookmarkService';
import { useAuth } from './AuthContext';
import { useUserEvent } from './UserEventContext';
import { SignupLoginPrompt } from '../components/common/SignupLoginPrompt';
import logger from '../utils/logger';

export interface SessionBookmarkContextValue {
  bookmarkedKeys: Set<string>;  // key = `${event_id}#${session_id}`
  loading: boolean;
  error: string | null;
  isBookmarked: (eventId: string, sessionId: string) => boolean;
  add: (eventId: string, sessionId: string) => Promise<void>;
  remove: (eventId: string, sessionId: string) => Promise<void>;
  refresh: (eventId?: string) => Promise<void>;
  showSnackbar: (message: string) => void;
  showSignupPrompt: () => void;
}

const SessionBookmarkContext = createContext<SessionBookmarkContextValue | undefined>(undefined);

const makeKey = (eventId: string, sessionId: string) => `${eventId}#${sessionId}`;

export const SessionBookmarkProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { refresh: refreshUserEvents } = useUserEvent();
  const [bookmarkedKeys, setBookmarkedKeys] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [signupPromptOpen, setSignupPromptOpen] = useState(false);

  const refresh = useCallback(async (eventId?: string) => {
    if (!user) {
      setBookmarkedKeys(new Set());
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await userBookmarkService.listMyBookmarks(eventId);
      // 想定外レスポンス (items 欠落) でも全体クラッシュさせず空配列扱いにする
      const items = res?.items ?? [];
      setBookmarkedKeys((prev) => {
        if (!eventId) {
          // 全取得: 完全置換
          return new Set(items.map((item) => makeKey(item.event_id, item.session_id)));
        }
        // event scope 取得: 当該 event の key のみ置換 (他 event の状態は保つ)
        const next = new Set<string>();
        prev.forEach((k) => {
          if (!k.startsWith(`${eventId}#`)) next.add(k);
        });
        items.forEach((item) => next.add(makeKey(item.event_id, item.session_id)));
        return next;
      });
    } catch (e) {
      logger.error('Failed to load bookmarks:', e);
      setError('ブックマーク一覧の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Refresh when auth state settles
  useEffect(() => {
    if (authLoading) return;
    refresh();
  }, [authLoading, user, refresh]);

  const isBookmarked = useCallback(
    (eventId: string, sessionId: string) => bookmarkedKeys.has(makeKey(eventId, sessionId)),
    [bookmarkedKeys]
  );

  const add = useCallback(async (eventId: string, sessionId: string) => {
    const key = makeKey(eventId, sessionId);
    // Optimistic update
    setBookmarkedKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    try {
      await userBookmarkService.addBookmark(eventId, sessionId);
      // 自動 MyEvent 紐付け反映 (AD-1、案 2 採用: backend が裏で put、frontend は refresh で同期)
      refreshUserEvents().catch((e) => logger.error('Failed to refresh user events after bookmark add:', e));
    } catch (e) {
      // Rollback
      setBookmarkedKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      throw e;
    }
  }, [refreshUserEvents]);

  const remove = useCallback(async (eventId: string, sessionId: string) => {
    const key = makeKey(eventId, sessionId);
    // Optimistic update
    setBookmarkedKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    try {
      await userBookmarkService.removeBookmark(eventId, sessionId);
    } catch (e) {
      // Rollback
      setBookmarkedKeys((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      throw e;
    }
  }, []);

  const showSnackbar = useCallback((message: string) => setSnackbarMessage(message), []);
  const showSignupPrompt = useCallback(() => setSignupPromptOpen(true), []);

  return (
    <SessionBookmarkContext.Provider value={{
      bookmarkedKeys, loading, error, isBookmarked, add, remove, refresh, showSnackbar, showSignupPrompt,
    }}>
      {children}
      <SignupLoginPrompt
        open={signupPromptOpen}
        onClose={() => setSignupPromptOpen(false)}
        title="セッションをブックマークするにはログインが必要です"
        message="ブックマークしたセッションは後でマイスケジュールから確認できます"
      />
      <Snackbar
        open={snackbarMessage !== null}
        message={snackbarMessage}
        onClose={() => setSnackbarMessage(null)}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </SessionBookmarkContext.Provider>
  );
};

export const useSessionBookmarkContext = (): SessionBookmarkContextValue => {
  const ctx = useContext(SessionBookmarkContext);
  if (!ctx) throw new Error('useSessionBookmarkContext must be used within SessionBookmarkProvider');
  return ctx;
};
